(() => {
  if (!window.limencyPillowPreviewSettings) return;

  // "Size guide" sits beside BonaPer's "View Photo Guide" and opens a to-scale
  // drawing: the customer's pillow (or a sample shape before upload) next to a
  // phone and a standard throw pillow. A size is the pillow's longest side.
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const PHONE = { w: 7.2, h: 15.5 }; // 6.1″ smartphone, cm
  const CUSHION = 45.7; // 18″ square throw pillow, cm
  const PERSON = { h: 170, w: 44 }; // 5′7″ adult, shown once a size passes 100 cm
  const MIN_SCALE_CM = 60; // the scene was drawn for this ruler height; text and strokes scale from it
  const GROUND = 66;
  const PILLOW_X = 24;
  const SAMPLE_ASPECT = 0.85;
  const SAMPLE_PATH = 'M0.1 0.25 L0.18 0 L0.36 0.16 L0.64 0.16 L0.82 0 L0.9 0.25 C1 0.45 1 0.75 0.92 0.9 Q0.85 1 0.5 1 Q0.15 1 0.08 0.9 C0 0.75 0 0.45 0.1 0.25 Z';

  let button, dialog, svg, ground, ruler, pillowGroup, marker, markerLabel, cushionGroup, personGroup, pills, readout, choose, sampleNote;
  let sizes = [], picked = null, shape = { key: '', aspect: SAMPLE_ASPECT, image: null };
  let scaleCm = MIN_SCALE_CM, showPerson = false;
  let scheduled = false;

  const el = (tag, attrs = {}, parent) => {
    const node = document.createElementNS(SVG_NS, tag);
    for (const [name, value] of Object.entries(attrs)) node.setAttribute(name, value);
    parent?.append(node);
    return node;
  };
  const sizeRadios = () => [...document.querySelectorAll('variant-selects input[type="radio"]')]
    .filter((input) => /cm\s*\/\s*[\d.]+\s*inch/i.test(input.value));

  function readSizes() {
    return sizeRadios().map((input) => {
      const match = input.value.match(/([\d.]+)\s*cm\s*\/\s*([\d.]+)\s*inch/i);
      const label = input.id ? document.querySelector(`label[for="${input.id}"]`) : null;
      return {
        value: input.value,
        cm: Number(match[1]),
        inches: Math.round(Number(match[2])),
        price: label?.querySelector('.pillow-size__price')?.textContent.trim() || '',
        popular: !!label?.querySelector('[data-pillow-size-popular]'),
      };
    }).sort((a, b) => a.cm - b.cm);
  }

  // The processed thumbnail is a cutout on transparent pixels; measure the
  // silhouette so its longest side, not the canvas, maps to the chosen size.
  function measureShape(src) {
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        try {
          const canvas = Object.assign(document.createElement('canvas'), { width: image.naturalWidth, height: image.naturalHeight });
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(image, 0, 0);
          const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
          let minX = width, minY = height, maxX = -1, maxY = -1;
          for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
            if (data[(y * width + x) * 4 + 3] > 24) {
              if (x < minX) minX = x; if (x > maxX) maxX = x;
              if (y < minY) minY = y; if (y > maxY) maxY = y;
            }
          }
          if (maxX < 0) return resolve(null);
          resolve({ src, width, height, box: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 } });
        } catch {
          resolve(null);
        }
      };
      image.onerror = () => resolve(null);
      image.src = src;
    });
  }

  async function refreshShape() {
    const ready = document.documentElement.classList.contains('bonaper-pillow-upload-first--ready');
    const thumb = document.querySelector('.bonaper-field[data-bonaper-field="pet-photo"] .bonaper-photo__thumb:not([hidden])');
    const src = ready && thumb?.getAttribute('src') || '';
    if (src === shape.key) return;
    shape = { key: src, aspect: SAMPLE_ASPECT, image: null };
    if (src) {
      const measured = await measureShape(src);
      if (measured && shape.key === src) shape = { key: src, aspect: measured.box.w / measured.box.h, image: measured };
    }
    if (dialog?.open) drawPillow();
  }

  function buildScene() {
    svg = el('svg', { class: 'pillow-size-modal__svg', role: 'img', 'aria-labelledby': 'PillowSizeGuideSceneTitle' });
    el('title', { id: 'PillowSizeGuideSceneTitle' }, svg).textContent = 'Pillow drawn to scale next to a phone and an 18 inch throw pillow';
    ground = el('line', { x1: 8, y1: GROUND, y2: GROUND, class: 'pillow-size-modal__ground' }, svg);
    // Ruler: centimetres on the left of the axis, rebuilt per open to fit the largest size.
    ruler = el('g', { class: 'pillow-size-modal__ruler', 'aria-hidden': 'true' }, svg);
    // Phone.
    const phone = el('g', { class: 'pillow-size-modal__ref', 'aria-hidden': 'true' }, svg);
    el('rect', { x: 12, y: GROUND - PHONE.h, width: PHONE.w, height: PHONE.h, rx: 1.2 }, phone);
    el('text', { x: 12 + PHONE.w / 2, y: GROUND + 5.5, 'text-anchor': 'middle' }, phone).textContent = 'Phone 6″';
    pillowGroup = el('g', { class: 'pillow-size-modal__pillow' }, svg);
    cushionGroup = el('g', { class: 'pillow-size-modal__ref', 'aria-hidden': 'true' }, svg);
    el('rect', { y: GROUND - CUSHION, width: CUSHION, height: CUSHION, rx: 4 }, cushionGroup);
    el('text', { y: GROUND + 5.5, 'text-anchor': 'middle' }, cushionGroup).textContent = '18″ throw pillow';
    personGroup = el('g', { class: 'pillow-size-modal__ref', 'aria-hidden': 'true' }, svg);
    el('circle', { cx: PERSON.w / 2, cy: GROUND - PERSON.h + 11, r: 11 }, personGroup);
    el('path', {
      d: `M${PERSON.w * 0.12} ${GROUND} L${PERSON.w * 0.12} ${GROUND - PERSON.h + 70} Q0 ${GROUND - PERSON.h + 70} 0 ${GROUND - PERSON.h + 52} L0 ${GROUND - PERSON.h + 36} Q0 ${GROUND - PERSON.h + 25} 11 ${GROUND - PERSON.h + 25} L${PERSON.w - 11} ${GROUND - PERSON.h + 25} Q${PERSON.w} ${GROUND - PERSON.h + 25} ${PERSON.w} ${GROUND - PERSON.h + 36} L${PERSON.w} ${GROUND - PERSON.h + 52} Q${PERSON.w} ${GROUND - PERSON.h + 70} ${PERSON.w * 0.88} ${GROUND - PERSON.h + 70} L${PERSON.w * 0.88} ${GROUND} Z`,
    }, personGroup);
    el('text', { x: PERSON.w / 2, y: GROUND + 5.5, 'text-anchor': 'middle' }, personGroup).textContent = 'Adult 5′7″';
    marker = el('line', { class: 'pillow-size-modal__marker', x1: 8 }, svg);
    markerLabel = el('text', { class: 'pillow-size-modal__marker-label', x: 10 }, svg);
  }

  // Fit the ruler to the largest size; past 100 cm an adult joins the scene so
  // a body-length pillow still reads at a glance.
  function buildRuler() {
    const maxCm = sizes.length ? sizes[sizes.length - 1].cm : 0;
    showPerson = maxCm > 100;
    scaleCm = Math.max(MIN_SCALE_CM, Math.ceil((Math.max(maxCm, showPerson ? PERSON.h : 0) + 2) / 10) * 10);
    const step = scaleCm > 100 ? 20 : 10;
    const k = scaleCm / MIN_SCALE_CM;
    ruler.replaceChildren();
    el('line', { x1: 8, x2: 8, y1: GROUND, y2: GROUND - scaleCm }, ruler);
    for (let cm = 0; cm <= scaleCm; cm += step) {
      el('line', { x1: 8 - 2 * k, x2: 8, y1: GROUND - cm, y2: GROUND - cm }, ruler);
      el('text', { x: 8 - 3 * k, y: GROUND - cm + k, 'text-anchor': 'end' }, ruler).textContent = `${cm}`;
    }
    el('text', { x: 8 - 3 * k, y: GROUND - scaleCm - 3 * k, 'text-anchor': 'end' }, ruler).textContent = 'cm';
    svg.querySelectorAll('.pillow-size-modal__ref text').forEach((label) => label.setAttribute('y', GROUND + 5.5 * k));
    personGroup.style.display = showPerson ? '' : 'none';
    svg.style.setProperty('--pillow-scale-k', String(k));
  }

  function drawPillow() {
    if (!picked) return;
    const k = scaleCm / MIN_SCALE_CM;
    const maxCm = sizes.length ? sizes[sizes.length - 1].cm : scaleCm;
    const longest = picked.cm;
    const height = shape.aspect > 1 ? longest / shape.aspect : longest;
    const width = height * shape.aspect;
    const maxWidth = (shape.aspect > 1 ? maxCm : maxCm * shape.aspect);
    pillowGroup.replaceChildren();
    if (shape.image) {
      const { box, width: iw, height: ih, src } = shape.image;
      const s = width / box.w;
      el('image', {
        href: src,
        x: PILLOW_X - box.x * s,
        y: GROUND - height - box.y * s,
        width: iw * s,
        height: ih * s,
        preserveAspectRatio: 'none',
      }, pillowGroup);
    } else {
      el('path', {
        d: SAMPLE_PATH,
        class: 'pillow-size-modal__sample',
        transform: `translate(${PILLOW_X} ${GROUND - height}) scale(${width} ${height})`,
        'vector-effect': 'non-scaling-stroke',
      }, pillowGroup);
    }
    const cushionX = PILLOW_X + maxWidth + 8;
    const personX = cushionX + CUSHION + 10 * k;
    const sceneRight = showPerson ? personX + PERSON.w : cushionX + CUSHION;
    ground.setAttribute('x2', sceneRight + 3);
    cushionGroup.querySelector('rect').setAttribute('x', cushionX);
    cushionGroup.querySelector('text').setAttribute('x', cushionX + CUSHION / 2);
    personGroup.setAttribute('transform', `translate(${personX} 0)`);
    const top = GROUND - height;
    marker.setAttribute('y1', top); marker.setAttribute('y2', top);
    marker.setAttribute('x2', PILLOW_X + width);
    markerLabel.setAttribute('y', top - 1.5 * k);
    markerLabel.textContent = `${picked.inches}″ · ${picked.cm} cm`;
    const left = 8 - 14 * k;
    svg.setAttribute('viewBox', `${left} ${GROUND - scaleCm - 10 * k} ${sceneRight + 10 - left} ${scaleCm + 18 * k}`);
    sampleNote.hidden = !!shape.image;
    pills.querySelectorAll('button').forEach((pill) => pill.setAttribute('aria-pressed', String(pill.dataset.value === picked.value)));
    readout.textContent = `${picked.inches}″ (${picked.cm} cm) on the longest side${picked.price ? ` · ${picked.price}` : ''}`;
    const current = sizeRadios().find((input) => input.checked)?.value === picked.value;
    choose.textContent = current ? 'Selected size' : `Choose ${picked.inches}″`;
    choose.disabled = false;
    choose.dataset.current = String(current);
  }

  function buildDialog() {
    dialog = document.createElement('dialog');
    dialog.className = 'pillow-size-modal';
    dialog.setAttribute('aria-labelledby', 'PillowSizeGuideTitle');
    dialog.innerHTML = `
      <div class="pillow-size-modal__head">
        <h2 id="PillowSizeGuideTitle" class="pillow-size-modal__title">Size guide</h2>
        <button type="button" class="pillow-size-modal__close" aria-label="Close size guide">×</button>
      </div>
      <p class="pillow-size-modal__intro">Size is the longest side of your pillow, shown to scale next to everyday objects.</p>
      <div class="pillow-size-modal__scene"></div>
      <p class="pillow-size-modal__sample-note">Sample shape. Upload your photo to see your own pillow here.</p>
      <div class="pillow-size-modal__sizes" role="group" aria-label="Pillow size"></div>
      <p class="pillow-size-modal__readout" aria-live="polite"></p>
      <button type="button" class="pillow-size-modal__choose button button--primary"></button>`;
    buildScene();
    dialog.querySelector('.pillow-size-modal__scene').append(svg);
    pills = dialog.querySelector('.pillow-size-modal__sizes');
    readout = dialog.querySelector('.pillow-size-modal__readout');
    choose = dialog.querySelector('.pillow-size-modal__choose');
    sampleNote = dialog.querySelector('.pillow-size-modal__sample-note');
    dialog.querySelector('.pillow-size-modal__close').addEventListener('click', () => dialog.close());
    // A click on the backdrop lands on the dialog element itself.
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    pills.addEventListener('click', (event) => {
      const pill = event.target.closest('button[data-value]');
      if (!pill) return;
      picked = sizes.find((size) => size.value === pill.dataset.value) || picked;
      drawPillow();
    });
    choose.addEventListener('click', () => {
      const value = picked?.value;
      dialog.close();
      const radio = sizeRadios().find((input) => input.value === value);
      if (radio && !radio.checked) radio.click();
      // The theme re-renders the picker on a variant change; focus the chosen
      // size once it has settled.
      setTimeout(() => sizeRadios().find((input) => input.value === value)?.focus(), 400);
    });
    dialog.addEventListener('close', () => button?.focus());
    document.body.append(dialog);
  }

  async function open() {
    if (!dialog) buildDialog();
    sizes = readSizes();
    if (!sizes.length) return;
    const checked = sizeRadios().find((input) => input.checked)?.value;
    picked = sizes.find((size) => size.value === checked) || sizes.find((size) => size.popular) || sizes[0];
    pills.replaceChildren(...sizes.map((size) => {
      const pill = document.createElement('button');
      pill.type = 'button';
      pill.dataset.value = size.value;
      pill.textContent = `${size.inches}″`;
      pill.setAttribute('aria-label', `${size.inches} inches, ${size.cm} centimetres`);
      return pill;
    }));
    buildRuler();
    await refreshShape();
    drawPillow();
    dialog.showModal();
  }

  function syncButton() {
    scheduled = false;
    const guide = document.querySelector('.bonaper-field[data-bonaper-field="pet-photo"] .bonaper-photo__guide');
    if (!guide) return;
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'pillow-size-guide-btn';
      button.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="7.5" width="19" height="9" rx="1.5"/><path d="M6.5 7.5v3M10 7.5v2M13.5 7.5v3M17 7.5v2"/></svg><span>Size guide</span>';
      button.setAttribute('aria-haspopup', 'dialog');
      button.addEventListener('click', open);
    }
    if (guide.nextElementSibling !== button) guide.after(button);
    refreshShape();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(syncButton, 60);
  }

  new MutationObserver(schedule).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'hidden'] });
  syncButton();
})();
